import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";

interface CohortGuardProps {
  /** The active user count for the current filter context. */
  userCount: number | null | undefined;
  /** Minimum number of participants required before showing data. Defaults to 2. */
  minCohort?: number;
  /**
   * When true, children are rendered as-is regardless of userCount.
   * Set this for central admins who can see all data.
   */
  bypass?: boolean;
  /**
   * When true, render children without applying the guard — used while the
   * user count is still being fetched to avoid a flash of the privacy overlay.
   */
  loading?: boolean;
  children: React.ReactNode;
}

/**
 * CohortGuard wraps any chart or data visualisation and prevents exposure of
 * data when the active user cohort is smaller than the configured minimum.
 *
 * - If `bypass` is true (central admin) → render children normally.
 * - If `loading` is true → render children normally (avoid flicker).
 * - If `userCount >= minCohort` → render children normally.
 * - Otherwise → render children with a blur overlay and a privacy notice.
 */
export const CohortGuard: React.FC<CohortGuardProps> = ({
  userCount,
  minCohort = 2,
  bypass = false,
  loading = false,
  children,
}) => {
  // Central admins and loading states always show the content.
  if (bypass || loading) {
    return <>{children}</>;
  }

  // If count is unknown (null/undefined) we treat it as insufficient to be safe.
  const count = userCount ?? 0;
  const isSufficient = count >= minCohort;

  if (isSufficient) {
    return <>{children}</>;
  }

  const plural = count === 1 ? "" : "s";

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Blurred content underneath */}
      <Box
        sx={{
          filter: "blur(6px)",
          pointerEvents: "none",
          userSelect: "none",
          width: "100%",
          height: "100%",
        }}
        aria-hidden="true"
      >
        {children}
      </Box>

      {/* Privacy overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 3,
            maxWidth: 340,
            textAlign: "center",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <PeopleOutlineIcon
            sx={{ fontSize: 40, color: "text.secondary" }}
          />
          <Typography variant="subtitle1" fontWeight={600}>
            Insufficient cohort size
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This chart requires at least {minCohort} participant
            {minCohort === 1 ? "" : "s"} with data. Currently {count} active
            user{plural} match the selected filters.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};
